"use client";

import React from "react";
import { Loader } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
function ContactForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center space-y-6 animate-in fade-in zoom-in duration-500">
        <div className="size-16 rounded-full bg-primary/10 flex items-center justify-center mb-4">
          <svg className="size-8 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h3 className="text-2xl font-bold font-default text-foreground uppercase tracking-wider">Message Sent</h3>
        <p className="text-muted-foreground max-w-md mx-auto font-default">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="mt-8 border-border hover:bg-accent rounded-full">
          Send Another Message
        </Button>
      </div>;
  }
  const inputStyles = "bg-white border-border text-foreground placeholder:text-muted-foreground/50 focus-visible:ring-primary/20 focus-visible:border-primary transition-all duration-300 rounded-none h-10";
  return <Form onSubmit={handleSubmit} className="space-y-6 !text-[rgba(255,255,255,1)]">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="first_name" rules={{
        required: "First name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-default text-sm font-medium">First Name*</FormLabel>
              <FormControl>
                <Input placeholder="John" className={inputStyles} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="last_name" rules={{
        required: "Last name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-default text-sm font-medium">Last Name*</FormLabel>
              <FormControl>
                <Input placeholder="Doe" className={inputStyles} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-default text-sm font-medium">Email*</FormLabel>
              <FormControl>
                <Input type="email" placeholder="john@example.com" className={inputStyles} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone" rules={{
        required: "Phone number is required",
        pattern: {
          value: /^[+]?[(]?[0-9]{3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{4,6}$/,
          message: "Please enter a valid phone number"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-default text-sm font-medium">Phone*</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="(519) 000-0000" className={inputStyles} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="message" rules={{
      required: "Message is required",
      minLength: {
        value: 10,
        message: "Message must be at least 10 characters"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground font-default text-sm font-medium">Message*</FormLabel>
            <FormControl>
              <Textarea placeholder="Tell us about your masonry project..." className={`min-h-[150px] resize-y ${inputStyles}`} {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-none flex items-start gap-3">
          <svg className="size-5 text-destructive shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p className="text-sm text-destructive/90">{submitError}</p>
        </div>}

      <div className="flex justify-end">
        <Button type="submit" disabled={isSubmitting} className="w-full md:w-auto h-12 px-10 text-base font-default uppercase tracking-widest bg-primary text-primary-foreground hover:bg-[#e92d2d] rounded-full transition-colors duration-300 group">
          {isSubmitting ? <>
              <Loader className="animate-spin mr-2 size-5" />
              Submitting...
            </> : <>
              Submit
              <svg className="size-4 ml-2 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
              </svg>
            </>}
        </Button>
      </div>
    </Form>;
}
export default function ReachOutToOurTeam() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="221943" id="start-your-project" className="bg-background py-24 px-4 relative overflow-hidden text-background">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-4xl h-full opacity-5 pointer-events-none">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,_var(--color-primary)_0%,_transparent_70%)] blur-3xl"></div>
      </div>
      <div className="absolute bottom-0 left-0 w-full h-px bg-border/20"></div>

      <div ref={ref} className={`max-w-4xl mx-auto relative z-10 transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
        <div className="text-center mb-16 space-y-6">
          <h2 className="text-4xl md:text-5xl font-serif tracking-wider text-foreground uppercase">
            CONTACT US
          </h2>
          <p className="text-base text-foreground max-w-2xl mx-auto font-default leading-relaxed">
            Whether it's a small repair or a full-scale masonry project, we look forward to learning more about how we can help. Our team typically responds within 1–2 business days. If your request is urgent, feel free to give us a call at 519-729-8035.
          </p>
        </div>

        <FormProvider formKey="start_project_form_home" formId="start_project_form_home" sectionName="ReachOutToOurTeam" className="bg-white p-8 md:p-12 rounded-none shadow-none relative overflow-hidden" defaultValues={{
        first_name: "",
        last_name: "",
        email: "",
        phone: "",
        message: ""
      }} validationRules={{
        first_name: {
          required: true
        },
        last_name: {
          required: true
        },
        email: {
          required: true,
          pattern: "email"
        },
        phone: {
          required: true,
          pattern: "phone"
        },
        message: {
          required: true
        }
      }} submitText="Submit" successMessage="Thank you for reaching out! Your message has been received and our team will get back to you shortly.">
          <div className="absolute inset-0 border border-border/10 pointer-events-none"></div>
          
          <ContactForm />
        </FormProvider>
      </div>
    </section>;
}